import React, { useState, useEffect, useMemo } from 'react';
import { Users, Eye, Clock, MapPin, Monitor, RefreshCw, Filter, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  useActiveUsers,
  useActiveUserCount,
  useActiveUsersStats,
  useDailyActivity,
  useDailyActivityHistory
} from '../../hooks/useActiveUser';
import { ActiveUser, DailyActivity } from '../../services/activeUser.service';
import { useRbac } from '../../context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';

const formatDateInputValue = (date: Date) => date.toISOString().split('T')[0];

export default function ActiveUsersDashboard() {
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [activityDate, setActivityDate] = useState<string>(() => formatDateInputValue(new Date()));
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);
  const [selectedHistoryUser, setSelectedHistoryUser] = useState<DailyActivity | null>(null);
  const [showActiveUsers, setShowActiveUsers] = useState<boolean>(true);
  const [showDailyActivity, setShowDailyActivity] = useState<boolean>(true);
  
  const { user } = useRbac();
  const userRole = user?.role || null;
  const isAdmin = userRole === 'ADMIN';

  const { 
    activeUsers, 
    loading, 
    error, 
    fetchActiveUsers, 
    fetchActiveUsersByRole,
    pagination: activePagination
  } = useActiveUsers();
  
  const { count: activeUserCount, loading: countLoading } = useActiveUserCount();
  const { stats, loading: statsLoading } = useActiveUsersStats({ enabled: isAdmin });
  const {
    activities: dailyActivities,
    loading: dailyLoading,
    error: dailyError,
    fetchDailyActivity,
    pagination: dailyPagination
  } = useDailyActivity();
  const {
    history,
    loading: historyLoading,
    error: historyError,
    fetchHistory
  } = useDailyActivityHistory();

  const roleOptions = useMemo(() => {
    switch (userRole) {
      case 'ADMIN':
        return [
          { value: 'all', label: 'All Roles' },
          { value: 'ADMIN', label: 'Admin' },
          { value: 'SHM', label: 'SHM' },
          { value: 'FBDE', label: 'FBDE' },
          { value: 'HM', label: 'HM' },
          { value: 'AGENCY', label: 'Agency' },
          { value: 'HR', label: 'HR' },
          { value: 'TPO', label: 'TPO' },
          { value: 'BDE', label: 'BDE' },
        ];
      case 'SHM':
      case 'FBDE':
        return [
          { value: 'all', label: 'All Roles' },
          { value: 'HM', label: 'Hiring Managers' },
          { value: 'AGENCY', label: 'Agency' },
          { value: 'HR', label: 'HR' }
        ];
      case 'HM':
      case 'AGENCY':
        return [
          { value: 'all', label: 'All Roles' },
          { value: 'HR', label: 'HR' }
        ];
      default:
        return [
          { value: 'all', label: 'All Roles' }
        ];
    }
  }, [userRole]);

  useEffect(() => {
    const validRoles = roleOptions.map(option => option.value);
    if (!validRoles.includes(selectedRole)) {
      setSelectedRole('all');
      fetchActiveUsers({ page: 1, limit: activePagination.limit });
    }
  }, [roleOptions, selectedRole, fetchActiveUsers, activePagination.limit]);
  
  useEffect(() => {
    fetchDailyActivity({
      date: activityDate,
      role: selectedRole === 'all' ? undefined : selectedRole,
      page: 1,
      limit: dailyPagination.limit
    });
  }, [activityDate, selectedRole, fetchDailyActivity, dailyPagination.limit]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      const params = { page: activePagination.page, limit: activePagination.limit };
      if (selectedRole === 'all') {
        fetchActiveUsers(params);
      } else {
        fetchActiveUsersByRole(selectedRole, params);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [
    autoRefresh,
    selectedRole,
    fetchActiveUsers,
    fetchActiveUsersByRole,
    activePagination.page,
    activePagination.limit
  ]);

  const handleRoleFilter = (role: string) => {
    setSelectedRole(role);
    const params = { page: 1, limit: activePagination.limit };
    if (role === 'all') {
      fetchActiveUsers(params);
    } else {
      fetchActiveUsersByRole(role, params);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActivityDate(event.target.value);
  };

  const handleOpenHistory = (activity: DailyActivity) => {
    setSelectedHistoryUser(activity);
    setHistoryDialogOpen(true);
    fetchHistory(activity.userId, { page: 1 });
  };

  const handleHistoryDialogChange = (open: boolean) => {
    setHistoryDialogOpen(open);
    if (!open) {
      setSelectedHistoryUser(null);
    }
  };

  const handleHistoryPageChange = (direction: 'prev' | 'next') => {
    if (!selectedHistoryUser) return;
    const currentPage = history.page || 1;
    const targetPage = direction === 'prev' ? currentPage - 1 : currentPage + 1;
    if (targetPage < 1 || targetPage > history.totalPages) return;
    fetchHistory(selectedHistoryUser.userId, { page: targetPage, limit: history.limit });
  };

  const handleActivePageChange = (direction: 'prev' | 'next') => {
    const targetPage =
      direction === 'prev' ? activePagination.page - 1 : activePagination.page + 1;
    if (targetPage < 1 || targetPage > activePagination.totalPages) return;

    const params = { page: targetPage, limit: activePagination.limit };
    if (selectedRole === 'all') {
      fetchActiveUsers(params);
    } else {
      fetchActiveUsersByRole(selectedRole, params);
    }
  };

  const handleDailyPageChange = (direction: 'prev' | 'next') => {
    const targetPage =
      direction === 'prev' ? dailyPagination.page - 1 : dailyPagination.page + 1;
    if (targetPage < 1 || targetPage > dailyPagination.totalPages) return;

    fetchDailyActivity({
      date: activityDate,
      role: selectedRole === 'all' ? undefined : selectedRole,
      page: targetPage,
      limit: dailyPagination.limit
    });
  };

  const formatLastActivity = (lastActivity: string) => {
    const now = new Date();
    const activityTime = new Date(lastActivity);
    const diffInSeconds = Math.floor((now.getTime() - activityTime.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m ago`;
    } else {
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'hr':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'fbde':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'tpo':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'shm':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'hm':
      case 'agency':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (lastActivity: string) => {
    const now = new Date();
    const activityTime = new Date(lastActivity);
    const diffInMinutes = Math.floor((now.getTime() - activityTime.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'bg-green-500'; // Very recent
    } else if (diffInMinutes < 3) {
      return 'bg-yellow-500'; // Recent
    } else {
      return 'bg-gray-400'; // Older
    }
  };

  const formatActiveDuration = (seconds: number) => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getLatestDeviceInfo = (
    devices?: { userAgent: string; deviceType: string; timestamp: string }[]
  ) => {
    if (!devices || devices.length === 0) return 'Unknown';
    const latest = devices[devices.length - 1];
    const deviceLabel = latest.deviceType
      ? latest.deviceType.charAt(0).toUpperCase() + latest.deviceType.slice(1).toLowerCase()
      : 'Unknown';

    const extractBrowser = (ua: string): string | null => {
      if (/edg\/([\d\.]+)/i.test(ua)) {
        const version = ua.match(/edg\/([\d\.]+)/i)?.[1]?.split('.')[0];
        return `Edge${version ? ` ${version}` : ''}`;
      }
      if (/chrome\/([\d\.]+)/i.test(ua) && !/edg\//i.test(ua) && !/opr\//i.test(ua)) {
        const version = ua.match(/chrome\/([\d\.]+)/i)?.[1]?.split('.')[0];
        return `Chrome${version ? ` ${version}` : ''}`;
      }
      if (/firefox\/([\d\.]+)/i.test(ua)) {
        const version = ua.match(/firefox\/([\d\.]+)/i)?.[1]?.split('.')[0];
        return `Firefox${version ? ` ${version}` : ''}`;
      }
      if (/safari\/([\d\.]+)/i.test(ua) && /version\/([\d\.]+)/i.test(ua) && !/chrome/i.test(ua)) {
        const version = ua.match(/version\/([\d\.]+)/i)?.[1]?.split('.')[0];
        return `Safari${version ? ` ${version}` : ''}`;
      }
      if (/opr\/([\d\.]+)/i.test(ua)) {
        const version = ua.match(/opr\/([\d\.]+)/i)?.[1]?.split('.')[0];
        return `Opera${version ? ` ${version}` : ''}`;
      }
      return null;
    };

    const extractOS = (ua: string): string | null => {
      if (/windows nt 10/i.test(ua)) return 'Windows 10';
      if (/windows nt 11/i.test(ua)) return 'Windows 11';
      if (/windows nt 6\.3/i.test(ua)) return 'Windows 8.1';
      if (/windows nt 6\.2/i.test(ua)) return 'Windows 8';
      if (/windows nt 6\.1/i.test(ua)) return 'Windows 7';
      if (/macintosh|mac os x/i.test(ua)) return 'macOS';
      if (/android/i.test(ua)) return 'Android';
      if (/iphone|ipad|ipod/i.test(ua)) return 'iOS';
      if (/linux/i.test(ua)) return 'Linux';
      return null;
    };

    const browser = extractBrowser(latest.userAgent || '');
    const os = extractOS(latest.userAgent || '');

    return [deviceLabel, browser, os].filter(Boolean).join(' · ') || deviceLabel;
  };

  return (
    <>
      <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Active Users</h1>
          <p className="text-gray-600">Monitor users currently active on the portal</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-50 border-green-200' : ''}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto Refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selectedRole === 'all') {
                fetchActiveUsers();
              } else {
                fetchActiveUsersByRole(selectedRole);
              }
            }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Active</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {countLoading ? '...' : activeUserCount}
            </div>
            <p className="text-xs text-muted-foreground">
              Users online now
            </p>
          </CardContent>
        </Card>

        {isAdmin && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Admins</CardTitle>
                <Users className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.byRole?.ADMIN || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Admin users active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">HR Users</CardTitle>
                <Users className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.byRole?.HR || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  HR users active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">FBDE Users</CardTitle>
                <Users className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.byRole?.FBDE || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  FBDE users active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">SHM Users</CardTitle>
                <Users className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.byRole?.SHM || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  SHM users active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">HM / Agency</CardTitle>
                <Users className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : (stats?.byRole?.HM || 0) + (stats?.byRole?.AGENCY || 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  HM and agency users active
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {roleOptions.length > 1 && (
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">Filter by Role:</span>
            <Select value={selectedRole} onValueChange={handleRoleFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
        </div>
        )}

        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium whitespace-nowrap">Activity Date:</span>
          <input
            type="date"
            value={activityDate}
            onChange={handleDateChange}
            className="border border-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            max={formatDateInputValue(new Date())}
          />
        </div>
      </div>

      {/* Active Users List */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Active Users ({activeUsers.length})
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowActiveUsers((prev) => !prev)}
            className="flex items-center gap-1"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                showActiveUsers ? '' : 'rotate-180'
              }`}
            />
          </Button>
        </CardHeader>
        <CardContent>
          {!showActiveUsers ? null : loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
              <span className="ml-2 text-gray-500">Loading active users...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  const params = { page: activePagination.page, limit: activePagination.limit };
                  if (selectedRole === 'all') {
                    fetchActiveUsers(params);
                  } else {
                    fetchActiveUsersByRole(selectedRole, params);
                  }
                }}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : activeUsers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No active users found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeUsers.map((user: ActiveUser) => (
                <div
                  key={user.userId}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Status Indicator */}
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(user.lastActivity)}`} />
                    
                    {/* User Info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{user.username}</h3>
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    {/* Current Page */}
                    <div className="flex items-center gap-1">
                      <Monitor className="h-4 w-4" />
                      <span className="max-w-32 truncate">
                        {user.currentPage || 'Unknown'}
                      </span>
                    </div>

                    {/* Last Activity */}
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatLastActivity(user.lastActivity)}</span>
                    </div>

                    {/* IP Address */}
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      <span className="font-mono text-xs">
                        {user.ipAddress}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {showActiveUsers &&
            !loading &&
            !error &&
            activeUsers.length > 0 &&
            activePagination.totalPages > 1 && (
              <div className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                <span>
                  Page {activePagination.page} of {activePagination.totalPages} ·{' '}
                  {activePagination.total} total user{activePagination.total === 1 ? '' : 's'}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={activePagination.page <= 1}
                    onClick={() => handleActivePageChange('prev')}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={activePagination.page >= activePagination.totalPages}
                    onClick={() => handleActivePageChange('next')}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
        </CardContent>
      </Card>

 

      {/* Daily Activity */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Daily Working Time
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Showing tracked activity for {new Date(activityDate).toLocaleDateString()}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDailyActivity((prev) => !prev)}
            className="flex items-center gap-1"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                showDailyActivity ? '' : 'rotate-180'
              }`}
            />
          </Button>
        </CardHeader>
        <CardContent>
          {!showDailyActivity ? null : dailyLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
              <span className="ml-2 text-gray-500">Calculating daily activity...</span>
            </div>
          ) : dailyError ? (
            <div className="text-center py-8">
              <p className="text-red-500">{dailyError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  fetchDailyActivity({
                    date: activityDate,
                    role: selectedRole === 'all' ? undefined : selectedRole,
                    page: dailyPagination.page,
                    limit: dailyPagination.limit
                  })
                }
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : dailyActivities.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No tracked activity for the selected date.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active Time
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      First Seen
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Seen
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dailyActivities.map((activity: DailyActivity) => (
                    <tr key={activity.userId}>
                      <td className="px-4 py-2">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{activity.username}</span>
                          <span className="text-xs text-gray-500">{activity.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Badge className={getRoleBadgeColor(activity.role)}>{activity.role}</Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{formatActiveDuration(activity.activeSeconds)}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{formatTime(activity.firstSeenAt)}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{formatTime(activity.lastSeenAt)}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {getLatestDeviceInfo(activity.devices)}
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenHistory(activity)}
                        >
                          View History
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {showDailyActivity &&
            !dailyLoading &&
            !dailyError &&
            dailyActivities.length > 0 &&
            dailyPagination.totalPages > 1 && (
              <div className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                <span>
                  Page {dailyPagination.page} of {dailyPagination.totalPages} ·{' '}
                  {dailyPagination.total} total record{dailyPagination.total === 1 ? '' : 's'}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={dailyPagination.page <= 1}
                    onClick={() => handleDailyPageChange('prev')}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={dailyPagination.page >= dailyPagination.totalPages}
                    onClick={() => handleDailyPageChange('next')}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
        </CardContent>
      </Card>


           {/* Popular Pages */}
          {showDailyActivity && stats?.byPage && Object.keys(stats.byPage).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Most Visited Pages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.byPage).map(([page, count]) => (
                <div key={page} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{page}</span>
                  <Badge variant="secondary">{count} users</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>

    <Dialog open={historyDialogOpen} onOpenChange={handleHistoryDialogChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedHistoryUser ? `${selectedHistoryUser.username}'s Activity History` : 'Activity History'}
            </DialogTitle>
            <DialogDescription>
              Review daily working time across previous sessions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {historyLoading ? (
              <div className="flex items-center justify-center py-6">
                <RefreshCw className="h-5 w-5 animate-spin text-gray-500" />
                <span className="ml-2 text-gray-500">Loading activity history...</span>
              </div>
            ) : historyError ? (
              <div className="text-center py-6">
                <p className="text-red-500">{historyError}</p>
                {selectedHistoryUser && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => fetchHistory(selectedHistoryUser.userId, { page: history.page })}
                  >
                    Retry
                  </Button>
                )}
              </div>
            ) : history.records.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                No historical activity captured yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active Time
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        First Seen
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Seen
                      </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device
                    </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.records.map((entry) => (
                      <tr key={entry.date}>
                        <td className="px-4 py-2 text-sm text-gray-800">
                          {new Date(entry.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {formatActiveDuration(entry.activeSeconds)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{formatTime(entry.firstSeenAt)}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{formatTime(entry.lastSeenAt)}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {getLatestDeviceInfo(entry.devices)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                Page {history.page} of {history.totalPages} · {history.total} day(s) recorded
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={history.page <= 1 || historyLoading}
                  onClick={() => handleHistoryPageChange('prev')}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={history.page >= history.totalPages || historyLoading}
                  onClick={() => handleHistoryPageChange('next')}
                >
                  Next
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  </>
  );
}
